import React from 'react';
import { motion } from 'framer-motion';
import { reveal } from '../utils/motion';
import { CaseStudyCard } from '../components/CaseStudyCard';
import { SiteHeader } from '../components/SiteHeader';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { caseStudies, heroMetrics } from '../data/caseStudies';
import { clientLogos } from '../data/clientLogos';

// Matches the "View Case Study" pill on the case study cards.
const pillButtonClass = 'inline-flex w-fit items-center whitespace-nowrap rounded-full bg-black px-3.5 py-1.5 text-[0.75rem] font-medium leading-5 text-white transition-[background-color,transform] duration-150 ease-out hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 active:scale-[0.97]';

// Dark grey → black, applied per line so it stays consistent across the underlined word.
const gradientText = 'bg-[linear-gradient(to_bottom,#4a4a4a,#000000)] bg-clip-text text-transparent [-webkit-box-decoration-break:clone] [box-decoration-break:clone]';
export function Home() {
  return <div className="relative min-h-screen w-full bg-white font-sans text-ink" data-id="element-141" data-mp="2vyq6txqmm254">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[760px] min-[700px]:h-[900px]" style={{
      backgroundImage: 'radial-gradient(rgba(0, 0, 0, 0.14) 1px, transparent 1.5px)',
      backgroundSize: '32px 32px',
      maskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)'
    }} data-id="element-142" data-mp="2qy2hdgi1ptd0">
        {/* Same grid, darker, revealed only inside a soft diagonal band that sweeps across. */}
        <div className="dot-wave absolute inset-0" data-id="element-143" data-mp="236y78byp70n6" />
      </div>
      <div className="relative mx-auto flex max-w-[1347px] flex-col gap-[30px] px-5 py-10 md:px-10" data-id="element-144" data-mp="1z64hdd1p3brn">
        <SiteHeader data-id="element-145" data-mp="1u9ns2firercz" />

        <main className="flex flex-col gap-5 min-[700px]:gap-[30px]" data-id="element-146" data-mp="1u94jvqiwvhc5">
          <section className="flex flex-col items-start gap-6 py-4 min-[700px]:flex-row min-[700px]:items-center min-[700px]:justify-between min-[700px]:gap-10 min-[700px]:pb-4 min-[700px]:pt-10 md:pb-10 md:pt-20" data-id="element-147" data-mp="1kjxon1rqev7f">
            <motion.img src="https://cdn.magicpatterns.com/uploads/oThdsUnkXC7FRqpUL19do3/Meikson-Square.jpg" alt="Portrait of Michael Meikson" className="aspect-square w-[160px] shrink-0 rounded-full object-cover min-[700px]:order-2 min-[700px]:w-[200px] lg:w-[240px]" initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={reveal} data-id="element-148" data-mp="1ao44aw7rtxf3" />
            <motion.div className="flex max-w-[960px] flex-1 flex-col gap-3 min-[700px]:order-1" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            ...reveal,
            delay: 0.05
          }} data-id="element-149" data-mp="1yk8dy0de81qh">
              <h1 className="text-[2rem] font-semibold leading-[1.05] tracking-[-0.02em] text-black min-[700px]:text-[3.5rem] lg:text-[5rem] lg:tracking-[-0.035em]" data-id="element-150" data-mp="2r243g4yslj1a">
                <span className={gradientText} data-id="element-151" data-mp="alo8niiu89xm">
                  18 years of experience across B2B SaaS, fintech, and healthcare.
                </span>
              </h1>
              <p className="text-subhead text-muted" data-id="element-152" data-mp="kcshe0hx6yk0">
                Specialize in driving business results through{' '}
                improved usability.
              </p>
            </motion.div>
          </section>

          <ul aria-label="Results" className="grid w-full grid-cols-1 sm:grid-cols-3 sm:gap-[30px]" data-id="element-153" data-mp="3nuaryfp2ujo4">
            {heroMetrics.map((m, i) => <motion.li key={m.label} className="flex flex-col border-black/10 py-5 [container-type:inline-size] [&:not(:first-child)]:border-t sm:py-7 sm:[&:not(:first-child)]:border-t-0" initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...reveal,
            delay: 0.15 + i * 0.06
          }} data-id="element-154" data-mp="o1p2iqlfu7b" data-mpi={i}>
                <span className="whitespace-nowrap text-[clamp(32px,26cqw,72px)] font-normal leading-[1.1] tracking-[-0.035em] text-positive max-[699px]:text-[48px]" data-id="element-155" data-mp="2ccg6qoy9soq8">
                  {m.value}
                </span>
                <span className="text-xl leading-[27px] tracking-[-0.02em] max-[699px]:text-base max-[699px]:leading-6" data-id="element-156" data-mp="2mc72sv2hbbsf">
                  {m.label}
                </span>
              </motion.li>)}
          </ul>

          <motion.section aria-label="Clients" className="-mt-5 border-y border-black/10 py-4 min-[700px]:-mt-[30px] min-[700px]:py-6" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          ...reveal,
          delay: 0.35
        }} data-id="element-157" data-mp="2va983mt8wqli">
            <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 md:flex-nowrap md:justify-between md:gap-x-6" data-id="element-158" data-mp="3a2b3ouoa8xfq">
              {clientLogos.map(logo => <li key={logo.name} className="flex shrink-0 items-center" data-id="element-159" data-mp="12g1essvy2boq">
                  <img src={logo.src} alt={logo.name} className="block h-[25px] w-auto max-w-full object-contain" data-id="element-160" data-mp="ycv8jl8u0ki9" />
                </li>)}
            </ul>
          </motion.section>

          <section aria-label="Case studies" className="flex flex-col gap-[30px]" data-id="element-161" data-mp="ofqjndnlqaxr">
            {caseStudies.map(study => <CaseStudyCard key={study.id} study={study} animate data-id="element-162" data-mp="1m1nx2wn4jq2u" />)}
          </section>

          <section className="mt-5 grid grid-cols-1 gap-[30px] border-t border-black/10 pt-10 min-[700px]:mt-[30px] min-[700px]:pt-[60px] md:grid-cols-2" data-id="element-163" data-mp="1h576zwwd71n4">
            <div className="flex flex-col gap-10" data-id="element-164" data-mp="17ecl4tn16427">
              <div className="flex flex-col gap-3" data-id="element-165" data-mp="17djmkfy86kv5">
                <h2 className="text-headline font-semibold" data-id="element-166" data-mp="24t71n7wsrc6z">
                  AI has become the core of my design practice.
                </h2>
                <p className="text-subhead text-muted" data-id="element-167" data-mp="2015shuuxe1l6">
                  I prototype in code and can ship working software.
                </p>
              </div>
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-168" data-mp="271m8c6y8kwp">
                <p data-id="element-169" data-mp="3kkstbl6m459j">
                  My primary design tool is Magic Patterns, where I maintain working prototypes of client projects. 
                </p>
                <a href="https://project-unified-inbox-with-filtering-117.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className={pillButtonClass} data-id="element-170" data-mp="3pib1vagpwih0">
                  View RSI Prototype →
                </a>
              </div>
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-171" data-mp="36l02zqyzzr23">
                <p data-id="element-172" data-mp="36l9rfa4e9q5o">
                  I built Percelo, an AEO (Answer Engine Optimization), tool solo in about two weeks with Claude Code. 
                </p>
                <a href="https://www.percelo.com" target="_blank" rel="noopener noreferrer" className={pillButtonClass} data-id="element-173" data-mp="14twogqlzlsc9">
                  Try Percelo →
                </a>
              </div>
            </div>
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[30px] md:aspect-auto md:h-full" data-id="element-174" data-mp="1nq5f5vsekkl6">
              <img src="https://cdn.magicpatterns.com/uploads/8x9mJt8jkvyUyDgp9GG3CK/Percelo-hero2.png" alt="Percelo AI visibility report open on a laptop" className="absolute inset-0 h-full w-full scale-[1.4] object-cover object-[50%_45%]" data-id="element-175" data-mp="1iydkb927rsp8" />
            </div>
          </section>

          <TestimonialsSection data-id="element-176" data-mp="26mevkpqs1n8c" />
        </main>

        <div className="pt-10" data-id="element-177" data-mp="22o7iq5zfp62f">
          <SiteHeader as="footer" data-id="element-178" data-mp="22l1s2mb5pw4l" />
        </div>
      </div>
    </div>;
}